// Authentication routes.
//
// There is no email provider in the mock, so wherever a real backend would email a code, the
// response carries `demoCode` and the UI shows it at that step (verify email, reset password,
// team invite). A real backend omits `demoCode` and sends the email instead — the contract is
// otherwise identical. Codes are single-use and expire (10 min verify / 30 min reset).
import {
  usersStore, tenantsStore, sessionsStore, codesStore, publicUser, roleIdForUser, tenantName, provisionTenant
} from "../core";
import {
  ok, fail, validationFail, genId, randomCode, randomToken, nowIso, slugify
} from "../helpers";
import { collect, emailError, firstMessage, lengthError, nameError, passwordError } from "../validate";

const VERIFY_TTL_MS = 10 * 60 * 1000;
const RESET_TTL_MS = 30 * 60 * 1000;
const norm = (email) => String(email || "").trim().toLowerCase();

function issueCode(kind, email, ttl) {
  const codes = codesStore.all();
  const code = randomCode();
  codes[`${kind}:${email}`] = { code, expiresAt: Date.now() + ttl };
  codesStore.save(codes);
  return code;
}

function checkCode(kind, email, code) {
  const codes = codesStore.all();
  const entry = codes[`${kind}:${email}`];
  if (!entry) return "missing";
  if (Date.now() > entry.expiresAt) return "expired";
  if (entry.code !== String(code || "").trim()) return "wrong";
  return "ok";
}

function consumeCode(kind, email) {
  const codes = codesStore.all();
  delete codes[`${kind}:${email}`];
  codesStore.save(codes);
}

function dropSessionsFor(userId) {
  const sessions = sessionsStore.all();
  Object.keys(sessions).forEach((token) => { if (sessions[token].userId === userId) delete sessions[token]; });
  sessionsStore.save(sessions);
}

function sessionPayload(user, token) {
  return {
    token, userId: user.id, name: user.name, email: user.email, tenantId: user.tenantId,
    tenantName: tenantName(user.tenantId), role: roleIdForUser(user), teamRole: user.teamRole
  };
}

export const authRoutes = [
  {
    method: "POST", pattern: /^\/api\/auth\/register$/, permission: null, tenant: false, auth: false,
    handler: ({ body }) => {
      const email = norm(body.email);
      const fields = collect([
        ["name", nameError(body.name, { label: "Full name" })],
        ["email", emailError(email)],
        ["password", passwordError(body.password)],
        ["companyName", lengthError(body.companyName, { max: 80, label: "Company name" })],
        ["inviteCode", body.inviteCode && !/^\d{6}$/.test(String(body.inviteCode).trim()) ? "Invite code is 6 digits." : ""]
      ]);
      if (fields) return validationFail(fields, firstMessage(fields));

      const users = usersStore.all();
      const existing = users.find((u) => u.email.toLowerCase() === email);

      // Joining an existing workspace with an invite code issued from the Team screen.
      if (body.inviteCode) {
        if (!existing || existing.status !== "Invited" || existing.inviteCode !== String(body.inviteCode).trim()) {
          return validationFail({ inviteCode: "That invite code is not valid for this email." });
        }
        existing.name = body.name.trim();
        existing.password = body.password;
        existing.status = "Active";
        existing.verified = true; // holding the invite code proves control of the address
        existing.lastActive = "Just now";
        delete existing.inviteCode;
        usersStore.save(users);
        return ok(201, { email, verificationRequired: false, joined: tenantName(existing.tenantId) });
      }

      if (existing) {
        if (existing.status === "Invited") {
          return validationFail({ inviteCode: "This email has a pending invitation — enter the invite code from your admin." });
        }
        return fail(409, "EMAIL_EXISTS", "An account with this email already exists.", { fields: { email: "An account with this email already exists." } });
      }

      // A new company = a brand-new tenant with this person as its Owner.
      const company = (body.companyName || "").trim() || `${body.name.trim()}'s Workspace`;
      const tenant = {
        id: `tenant-${slugify(company).slice(0, 24) || "workspace"}-${Date.now().toString(36).slice(-4)}`,
        name: company, plan: "Starter", region: "ap-south-1"
      };
      tenantsStore.save([...tenantsStore.all(), tenant]);
      provisionTenant(tenant, email, company);
      usersStore.save([...users, {
        id: genId("u"), tenantId: tenant.id, name: body.name.trim(), email, password: body.password,
        teamRole: "Owner", status: "Active", verified: false, lastActive: "Just now"
      }]);
      return ok(201, { email, verificationRequired: true, demoCode: issueCode("verify", email, VERIFY_TTL_MS) });
    }
  },
  {
    method: "POST", pattern: /^\/api\/auth\/verify-email$/, permission: null, tenant: false, auth: false,
    handler: ({ body }) => {
      const email = norm(body.email);
      if (!/^\d{6}$/.test(String(body.code || ""))) return validationFail({ code: "Enter the 6-digit code." });
      const users = usersStore.all();
      const user = users.find((u) => u.email.toLowerCase() === email);
      if (!user) return validationFail({ code: "That code is incorrect or has expired." });
      const result = checkCode("verify", email, body.code);
      if (result !== "ok") {
        const message = result === "expired" ? "That code has expired. Request a new one." : "That code is incorrect.";
        return fail(422, "CODE_INVALID", message, { fields: { code: message } });
      }
      user.verified = true;
      usersStore.save(users);
      consumeCode("verify", email);
      return ok(200, { verified: true });
    }
  },
  {
    method: "POST", pattern: /^\/api\/auth\/resend-code$/, permission: null, tenant: false, auth: false,
    handler: ({ body }) => {
      const email = norm(body.email);
      if (emailError(email)) return validationFail({ email: emailError(email) });
      const user = usersStore.all().find((u) => u.email.toLowerCase() === email);
      // Same shape either way so the endpoint never reveals whether an account exists.
      if (!user || user.verified) return ok(200, { sent: true, demoCode: randomCode() });
      return ok(200, { sent: true, demoCode: issueCode("verify", email, VERIFY_TTL_MS) });
    }
  },
  {
    method: "POST", pattern: /^\/api\/auth\/login$/, permission: null, tenant: false, auth: false,
    handler: ({ body }) => {
      const email = norm(body.email);
      const fields = collect([["email", emailError(email)], ["password", body.password ? "" : "Password is required."]]);
      if (fields) return validationFail(fields, firstMessage(fields));

      const users = usersStore.all();
      const user = users.find((u) => u.email.toLowerCase() === email && u.password && u.password === body.password);
      if (!user) return fail(401, "INVALID_CREDENTIALS", "Invalid email or password.");
      if (user.status === "Deactivated") return fail(403, "ACCOUNT_DEACTIVATED", "This account has been deactivated. Contact your workspace owner.");
      if (!user.verified) return fail(403, "EMAIL_NOT_VERIFIED", "Verify your email before signing in.", { email: user.email });

      const token = `tok_${user.id}_${randomToken().slice(0, 12)}`;
      const sessions = sessionsStore.all();
      sessions[token] = { userId: user.id, tenantId: user.tenantId, createdAt: nowIso() };
      sessionsStore.save(sessions);
      user.lastActive = "Just now";
      usersStore.save(users);
      return ok(200, sessionPayload(user, token));
    }
  },
  {
    method: "POST", pattern: /^\/api\/auth\/logout$/, permission: null, tenant: false, auth: false,
    handler: ({ token }) => {
      if (token) {
        const sessions = sessionsStore.all();
        delete sessions[token];
        sessionsStore.save(sessions);
      }
      return ok(200, { loggedOut: true });
    }
  },
  {
    method: "GET", pattern: /^\/api\/auth\/me$/, permission: null, tenant: false,
    handler: ({ userId }) => {
      const user = usersStore.all().find((u) => u.id === userId);
      if (!user) return fail(401, "SESSION_EXPIRED", "Session expired.");
      return ok(200, { ...publicUser(user), tenantName: tenantName(user.tenantId) });
    }
  },
  {
    method: "POST", pattern: /^\/api\/auth\/forgot-password$/, permission: null, tenant: false, auth: false,
    handler: ({ body }) => {
      const email = norm(body.email);
      if (emailError(email)) return validationFail({ email: emailError(email) }, emailError(email));
      const user = usersStore.all().find((u) => u.email.toLowerCase() === email && u.password);
      // Always "succeeds" with the same shape — a code for an unknown email simply never works.
      return ok(200, { sent: true, demoCode: user ? issueCode("reset", email, RESET_TTL_MS) : randomCode() });
    }
  },
  {
    method: "POST", pattern: /^\/api\/auth\/reset-password$/, permission: null, tenant: false, auth: false,
    handler: ({ body }) => {
      const email = norm(body.email);
      const fields = collect([
        ["code", /^\d{6}$/.test(String(body.code || "")) ? "" : "Enter the 6-digit reset code."],
        ["password", passwordError(body.password)],
        ["confirmPassword", body.password && body.password !== body.confirmPassword ? "Passwords do not match." : ""]
      ]);
      if (fields) return validationFail(fields, firstMessage(fields));

      const result = checkCode("reset", email, body.code);
      if (result !== "ok") {
        const message = result === "expired" ? "This reset code has expired. Request a new one." : "That reset code is incorrect or was already used.";
        return fail(422, "CODE_INVALID", message, { fields: { code: message } });
      }
      const users = usersStore.all();
      const user = users.find((u) => u.email.toLowerCase() === email);
      if (!user) return fail(404, "NOT_FOUND", "Account not found.");
      user.password = body.password;
      usersStore.save(users);
      consumeCode("reset", email);
      dropSessionsFor(user.id); // a password reset signs out every existing session
      return ok(200, { reset: true });
    }
  },
  {
    method: "POST", pattern: /^\/api\/auth\/deactivate$/, permission: null, tenant: false,
    handler: ({ userId, body }) => {
      if (String(body.confirmation || "").trim().toUpperCase() !== "DEACTIVATE") {
        return validationFail({ confirmation: "Please type DEACTIVATE correctly." }, "Please type DEACTIVATE correctly.");
      }
      const users = usersStore.all();
      const user = users.find((u) => u.id === userId);
      if (!user) return fail(401, "SESSION_EXPIRED", "Session expired.");
      if (user.teamRole === "Owner") {
        return fail(409, "OWNER_CANNOT_DEACTIVATE", "The workspace Owner can't deactivate their own account. Transfer ownership first.");
      }
      user.status = "Deactivated";
      usersStore.save(users);
      dropSessionsFor(user.id);
      return ok(200, { deactivated: true });
    }
  }
];
